/**
 * AgentUpgradePrompt
 *
 * Shown on the agent screens (Repairs, Relay simulator, the agent queues) when agent support
 * is not effectively active (BR-101/BR-102). Explains what agents do, the price and the one
 * next action; existing cases stay readable beneath it.
 *
 *   paid plan, add-on off   "Let agents handle repair requests" + price + "Turn on agent support"
 *                           (opens the turn-on dialog on the billing page)
 *   free trial              "Agent support comes with a paid plan" + "Choose a plan"
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bot } from 'lucide-react';
import { useBillingSubscription } from '../../hooks/useBilling';
import { formatUsd } from '../../api/billing';
import { Button } from '../ui/button';
import { AgentSupportPoints } from './AgentSupportPoints';
import { Skeleton } from '../ui/skeleton';

interface AgentUpgradePromptProps {
  /** Page-specific heading for the paid-plan variant. */
  title?: string;
  /** Show "Your past repair cases are still below, read-only." (pages that list existing cases). */
  showPastCasesNote?: boolean;
}

export function AgentUpgradePrompt({
  title = 'Let agents handle repair requests',
  showPastCasesNote = false,
}: AgentUpgradePromptProps) {
  const { data: subscription, isLoading } = useBillingSubscription();
  const [showHow, setShowHow] = useState(false);

  if (isLoading || !subscription) {
    return <Skeleton className="h-48 w-full rounded-xl" data-testid="agent-upgrade-prompt-loading" />;
  }

  const isTrial = subscription.agentAddon.ineligibleReason === 'trial';
  const priceCents = subscription.agentUnitPriceCents;
  const units = subscription.unitCount;
  const canTurnOn = subscription.agentAddon.eligible;

  return (
    <section
      className="rounded-xl border border-border bg-card p-[var(--card-padding)] flex flex-col gap-3"
      aria-labelledby="agent-upgrade-title"
      data-testid="agent-upgrade-prompt"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
          <Bot className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
        </div>
        <div className="flex flex-col gap-2">
          <h2 id="agent-upgrade-title" className="text-lg font-semibold text-foreground m-0">
            {isTrial ? 'Agent support comes with a paid plan' : title}
          </h2>
          {isTrial ? (
            <p className="m-0 text-sm text-muted-foreground" data-testid="agent-upgrade-description">
              Choose a plan, then add agent support
              {priceCents !== null ? ` for ${formatUsd(priceCents)} per unit / month` : ''}. Your trial still lets you
              publish listings with the AI leasing agent.
            </p>
          ) : (
            <>
              <AgentSupportPoints testId="agent-upgrade-description" />
              {priceCents !== null && (
                <p className="m-0 text-sm text-foreground" data-testid="agent-upgrade-price">
                  <strong>{formatUsd(priceCents)}</strong> per unit / month
                  <span className="text-muted-foreground">
                    {' '}
                    · {units} unit{units === 1 ? '' : 's'} = {formatUsd(priceCents * units)}/month
                  </span>
                </p>
              )}
            </>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2 md:flex-row md:items-center">
        {isTrial ? (
          <Button asChild className="min-h-[44px]">
            <Link to="/settings/billing" data-testid="agent-upgrade-choose-plan">
              Choose a plan
            </Link>
          </Button>
        ) : (
          <>
            <Button asChild className="min-h-[44px]">
              <Link
                to={canTurnOn ? '/settings/billing?turnOnAgentSupport=1' : '/settings/billing'}
                data-testid="agent-upgrade-turn-on"
              >
                {canTurnOn ? 'Turn on agent support' : 'Go to billing'}
              </Link>
            </Button>
            <Button
              type="button"
              variant="outline"
              className="min-h-[44px]"
              data-testid="agent-upgrade-how-it-works"
              aria-expanded={showHow}
              onClick={() => setShowHow((v) => !v)}
            >
              How it works
            </Button>
          </>
        )}
      </div>

      {showHow && (
        <p className="m-0 text-sm text-muted-foreground" data-testid="agent-upgrade-how-copy">
          Turning on agent support charges today&apos;s prorated amount, then adds it to your monthly bill. You can turn
          it off any time; it stays on until the end of that billing period.
        </p>
      )}

      {showPastCasesNote && !isTrial && (
        <p className="m-0 text-xs text-muted-foreground" data-testid="agent-upgrade-past-cases">
          Your past repair cases are still below, read-only.
        </p>
      )}
    </section>
  );
}
